/**
 * Module: screens/creator/ProfileEditScreen
 * Role: Route /creator-profile (FTR-1458) — creator's own profile: socials +
 *       audience. GET /creator/profile on mount; add/remove socials and PATCH of
 *       the content part via mutations that invalidate ['creator','profile'].
 *       Chrome matches the app's screen
 *       language (IOSScreen + PageHeader), not the onboarding wizard chrome —
 *       this is a settings-style screen, reachable after onboarding is done.
 *
 * States:
 *   isPending → simple loading text (screen is settings-weight, no skeleton needed)
 *   isError   → honest EmptyState ("Не получилось загрузить профиль")
 *   socials.length === 0 → neutral EmptyState ("Площадок пока нет") + add-editor below
 *   socials.length > 0   → SocialLinksEditor renders rows + add-editor
 *
 * Error text (BUG-1153): rendered VERBATIM from the server's `message` field
 * (Russian since Task 1.3) — this screen never invents its own wording, only
 * a neutral fallback when the server gave nothing usable.
 *
 * Uses:
 *   - api/creator: getCreatorProfile, updateCreatorProfile, addCreatorSocial,
 *     removeCreatorSocial, SocialNetwork, CreatorProfileUpdate
 *   - ./_SocialLinksEditor: SocialLinksEditor
 *   - ./_AudienceEditor: AudienceEditor
 *   - @tanstack/react-query: useQuery, useMutation, useQueryClient
 *   - components/atoms: IOSScreen, PageHeader, EmptyState
 *   - react-router-dom: useNavigate
 *
 * Used by: App.tsx (route /creator-profile)
 * Glossary: ai/glossary/buyer.md
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { IOSScreen, PageHeader, EmptyState, Ic } from '../../components/atoms';
import {
  addCreatorSocial,
  getCreatorProfile,
  removeCreatorSocial,
  updateCreatorProfile,
  type CreatorProfileUpdate,
  type SocialNetwork,
} from '../../api/creator';
import { SocialLinksEditor } from './_SocialLinksEditor';
import { AudienceEditor } from './_AudienceEditor';
import { P } from '../../styles/tokens';

const GENERIC_ERROR = 'Не получилось сохранить. Попробуй ещё раз.';

/**
 * BUG-1153: only Cyrillic backend text is presentable — English/machine codes are not.
 * Second copy of this exact filter: `steps/CitiesStep.tsx:handleSubmit` (inlined there,
 * not extracted — that file is outside this task's Allowed Files). If BUG-1153 needs a
 * third variant, extract both into one shared helper instead of copying a third time.
 */
function presentableMessage(err: unknown): string {
  const e = err as (Error & { userMessage?: string }) | undefined;
  const msg = e?.userMessage;
  return msg && /[а-яё]/i.test(msg) ? msg : GENERIC_ERROR;
}

export default function ProfileEditScreen() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [actionError, setActionError] = useState<string | null>(null);
  const [profileError, setProfileError] = useState<string | null>(null);

  const { data, isPending, isError } = useQuery({
    queryKey: ['creator', 'profile'],
    queryFn: getCreatorProfile,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['creator', 'profile'] });

  const addMutation = useMutation({
    mutationFn: ({ network, url }: { network: SocialNetwork; url: string }) =>
      addCreatorSocial(network, url),
    onSuccess: () => {
      setActionError(null);
      invalidate();
    },
    onError: (err: unknown) => setActionError(presentableMessage(err)),
  });

  const removeMutation = useMutation({
    mutationFn: (network: SocialNetwork) => removeCreatorSocial(network),
    onSuccess: () => {
      setActionError(null);
      invalidate();
    },
    onError: (err: unknown) => setActionError(presentableMessage(err)),
  });

  // PATCH контентной части: серверный ответ той же формы, что у GET, поэтому
  // кладём его прямо в кэш — экран не мигает вторым запросом.
  const profileMutation = useMutation({
    mutationFn: (patch: CreatorProfileUpdate) => updateCreatorProfile(patch),
    onSuccess: (fresh) => {
      setProfileError(null);
      queryClient.setQueryData(['creator', 'profile'], fresh);
    },
    onError: (err: unknown) => setProfileError(presentableMessage(err)),
  });

  const busy = addMutation.isPending || removeMutation.isPending;
  const socials = data?.socials ?? [];
  const availableNetworks = data?.available_networks ?? [];

  return (
    <IOSScreen>
      <PageHeader title="Профиль креатора" subtitle="Площадки и аудитория" onBack={() => navigate(-1)} />

      {isPending && (
        <div style={{ padding: '24px 16px', color: P.inkSoft }}>Загружаем…</div>
      )}

      {isError && !isPending && (
        <EmptyState
          icon={<Ic name="warning" size={28} color={P.muted} />}
          title="Не получилось загрузить профиль"
          subtitle="Попробуй открыть экран ещё раз"
        />
      )}

      {!isPending && !isError && (
        <div style={{ padding: '8px 16px 32px' }}>
          {socials.length === 0 && (
            <EmptyState
              icon={<Ic name="tag" size={28} color={P.muted} />}
              title="Площадок пока нет"
              subtitle="Добавьте ссылку на профиль или канал ниже"
            />
          )}
          <SocialLinksEditor
            socials={socials}
            availableNetworks={availableNetworks}
            busy={busy}
            error={actionError}
            onAdd={(network, url) => addMutation.mutate({ network, url })}
            onRemove={(network) => removeMutation.mutate(network)}
          />
          <AudienceEditor
            key={JSON.stringify(data?.profile ?? null)}
            profile={data?.profile ?? null}
            busy={profileMutation.isPending}
            error={profileError}
            onSave={(patch) => profileMutation.mutate(patch)}
          />
        </div>
      )}
    </IOSScreen>
  );
}
